import { Flex, Tooltip, Typography } from "antd";
import { NumberedList } from "components/NumberedList";
import {
  BID_REQUEST_STATUS_ENUM,
  BidEntity,
  DIRECTION_TEXT,
  NETWORK_TEXT,
  QUOTE_SELECTION_ENUM,
  QuoteAcceptData,
} from "interfaces";
import { BankInfoSelect } from "pages/BidInfoPage/BidOperations/BankInfo";
import { memo, useState } from "react";
import { UserStore } from "stores/UserStore";
import { checkDaysPassed, dateFormat, formatWithDecimals, notificationPeriodDays, truncateText } from "utils";
import { DIRECT_PAYMENT_CUSTOMER_LP, QUOTE_SELECTION } from "utils/envConstants";

type MessageType = {
  bid: BidEntity;
  user: UserStore;
  rate: string;
  destinationWallet?: string;
  messageHash?: string;
  showLpInfo?: boolean;
  showCustomerInfo?: boolean;
  quoteModal?: boolean;
  operationModal?: boolean;
  quoteRequestData?: QuoteAcceptData;
};

export const defaultMessage = ({
  bid,
  user,
  rate,
  destinationWallet,
  quoteModal,
  operationModal,
  quoteRequestData,
}: MessageType) => {
  const { userInfo: userInformation, roles } = user;
  const userInfo = userInformation!;

  const { isSeniorLP, isJuniorLP, isCustomer } = roles;
  const isProvider = isSeniorLP || isJuniorLP;

  const isLpProviderQuoteModal = isProvider && quoteModal;
  const isLpProviderOperationModal = isProvider && operationModal;

  // Делаем доп проверку destinationWallet для ПЛ (Экспорт) и не отправляем при УВЭД (стр/млд/ИМП)
  let destinationWalletParams = destinationWallet;
  let transitWalletParams = undefined;

  if (isLpProviderOperationModal && bid.contract.direction === "EXP") {
    destinationWalletParams = bid.destinationWallet;
  }

  if (isProvider && quoteModal && bid.contract.direction === "IMP") {
    transitWalletParams = bid.transitWallet;
  }

  // TODO: Подумать над более хорошей реализацией
  if (isCustomer) {
    if (bid.contract.direction === "EXP") transitWalletParams = bid.transitWallet;
    if (bid.contract.direction === "EXP" && bid.status === BID_REQUEST_STATUS_ENUM.FUNDING && operationModal)
      transitWalletParams = undefined;
    if (bid.contract.direction === "IMP" && bid.status === BID_REQUEST_STATUS_ENUM.FUNDING)
      destinationWalletParams = bid.destinationWallet;
    if (bid.contract.direction === "IMP" && bid.status === "QUOTE_PROPOSED")
      destinationWalletParams = bid.destinationWallet;
    if (bid.contract.direction === "IMP" && bid.status === "NEW" && QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER)
      destinationWalletParams = bid.destinationWallet;
  }

  const lpCompRegNumParams =
    quoteRequestData?.lpCompRegNum || (isProvider ? userInfo.company_reg_num : bid.quote?.lpCompRegNum);

  const lpComNameParams =
    quoteRequestData?.lpCompanyName || (isProvider ? userInfo.company_name : bid.quote?.lpCompanyName);

  return {
    //Заявка и договор
    requestId: bid.id,
    hid: bid.hid,
    unk: isLpProviderQuoteModal || isLpProviderOperationModal ? undefined : bid.contract.unk,
    direction: bid.contract.direction,

    // Стороны договора
    // Проверяем что если роль ПЛ и одна из модалок, то убираем параметр
    companyName: isLpProviderQuoteModal || isLpProviderOperationModal ? undefined : bid.contract.companyName,
    // Проверяем что если роль ПЛ и одна из модалок, то убираем параметр
    customerRegistrationNumber:
      isLpProviderQuoteModal || isLpProviderOperationModal ? undefined : bid.contract.customerRegistrationNumber,
    lpCompanyName: isCustomer ? undefined : lpComNameParams,
    lpCompRegNum: isCustomer ? undefined : lpCompRegNumParams,
    destinationWallet: destinationWalletParams?.length !== 0 ? destinationWalletParams : undefined,
    transitWallet: transitWalletParams,

    // Параметры сделки
    amount: bid.amount,
    currency: bid.currency,
    rate: quoteRequestData?.rate ? quoteRequestData.rate : rate,
    amountRub: (bid.amount ?? 0) * (quoteRequestData?.rate ? +quoteRequestData.rate : +rate),

    //данные о пользователе
    userId: userInfo.id,
    name: userInfo.name,
    secondName: userInfo.second_name,
    surname: userInfo.surname,

    timeStamp: new Date(),
  };
};

export const defaultPageMessage = ({
  bid,
  user,
  rate,
  destinationWallet,
  quoteModal,
  operationModal,
  quoteRequestData,
}: MessageType): Record<string, string> => {
  const message = defaultMessage({
    bid,
    user,
    rate,
    destinationWallet,
    quoteModal,
    operationModal,
    quoteRequestData,
  });
  const { isCustomer } = user.roles;

  const { isSeniorLP, isJuniorLP } = user.roles;
  const isProvider = isSeniorLP || isJuniorLP;

  /**
   * Функция запрашивает адрес кошелька в зависимости от направления контракта и роли
   */
  const getTransitWalletAddress = () => {
    const { direction } = bid.contract;
    const destWallet = destinationWallet || bid.destinationWallet;

    if (!isProvider && !isCustomer) return "";

    //Роль ПЛ
    if (isProvider) {
      if (direction === "EXP" && destWallet) return destWallet;
      if (direction === "IMP" && bid.transitWallet && !operationModal) return bid.transitWallet;
      return "";
    }

    //Роль УВЭД
    if (isCustomer) {
      if (
        direction === "EXP" &&
        bid.status === BID_REQUEST_STATUS_ENUM.FUNDING &&
        operationModal &&
        QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER
      )
        return "";
      if (bid.contract.direction === "EXP" && bid.status === BID_REQUEST_STATUS_ENUM.FUNDING && operationModal)
        return "";

      if (direction === "IMP" && bid.status === BID_REQUEST_STATUS_ENUM.FUNDING && operationModal) return destWallet;
      if (direction === "IMP" && bid.status === "QUOTE_PROPOSED") return destWallet;
      if (
        bid.contract.direction === "IMP" &&
        bid.status === "NEW" &&
        QUOTE_SELECTION === QUOTE_SELECTION_ENUM.CUSTOMER &&
        operationModal
      )
        return destWallet;
      if (direction === "EXP" && bid.transitWallet) return bid.transitWallet;
      if (direction === "IMP" && destWallet) return destWallet;

      return "";
    }
    return "";
  };

  const transitWalletAddress = getTransitWalletAddress();
  const transitWallet = transitWalletAddress ? truncateText(transitWalletAddress, 10, 10) : "";

  return {
    hid: message.hid.toString(),
    unk: message.unk ?? "",
    direction: DIRECTION_TEXT[message.direction],

    companyName: message.companyName ?? "",
    customerRegistrationNumber: message.customerRegistrationNumber ?? "",
    lpCompanyName: message.lpCompanyName ?? "",
    lpCompRegNum: message.lpCompRegNum ?? "",
    destinationWallet: message.destinationWallet ?? "",

    amount: formatWithDecimals(message.amount?.toString()) ?? "",
    amountRub: formatWithDecimals(message.amountRub?.toString()) ?? "",
    currency: message.currency ?? "",
    rate: quoteRequestData ? formatWithDecimals(quoteRequestData.rate) : rate ? formatWithDecimals(rate) : "",
    //В зависимости от условия сюда падают кошельки dest и transit для модального окна
    //смотреть getTransitWalletAddress()
    transitWallet: `${transitWallet}`,

    user: message.name + " " + (message.secondName ? message.secondName + " " : "") + message.surname,

    date: dateFormat(message.timeStamp),
  };
};

export const PageMessage = memo(
  ({
    bid,
    user,
    rate,
    destinationWallet,
    messageHash,
    showLpInfo = false,
    quoteModal = false,
    operationModal = false,
    showCustomerInfo = false,
    quoteRequestData,
  }: MessageType) => {
    const message = defaultPageMessage({
      bid,
      user,
      rate,
      destinationWallet,
      quoteModal,
      operationModal,
      quoteRequestData,
    });

    const { isCustomer, isJuniorLP, isSeniorLP, isSeniorOperator } = user.roles;
    const { direction } = bid.contract;
    const isImp = direction === "IMP";
    const isExp = direction === "EXP";

    const isSeniorOperatorDaysPassed =
      checkDaysPassed(bid.created, notificationPeriodDays).isOverDays &&
      !bid.requestProcessing.fiatReceived &&
      bid.requestProcessing.rubReceivedByPlatform &&
      isSeniorOperator;

    const showBankInfo =
      (((isCustomer || isSeniorOperatorDaysPassed) && isExp) || ((isJuniorLP || isSeniorLP) && isImp)) &&
      !DIRECT_PAYMENT_CUSTOMER_LP &&
      bid.status === "FUNDING";

    const messageItems = Object.entries(message)
      .map(([key, value]) => {
        const msg = getMessage(key, value, bid, user, showLpInfo, showCustomerInfo, quoteModal);
        return msg ?? undefined;
      })
      .filter((item) => item !== undefined);

    return (
      <Flex vertical>
        <NumberedList items={messageItems} />
        {showBankInfo && <BankInfoSelect showDefaultValue />}
        {messageHash && (
          <Tooltip title="Хэш сообщения для контроля целостности, в смс подтверждении будет указан этот же код">
            <Typography.Text type="secondary">
              Универсальный код операции: {truncateText(messageHash, 10, 10)}
            </Typography.Text>
          </Tooltip>
        )}
      </Flex>
    );
  }
);

const getMessage = (
  key: string,
  value: string,
  bid: BidEntity,
  user: UserStore,
  showLpInfo: boolean,
  showCustomerInfo: boolean,
  quoteModal: boolean
) => {
  const labels: Record<string, string> = {
    hid: "Номер заявки",
    unk: !showLpInfo ? "Договор УНК" : "",
    direction: "Тип договора",
    companyName: !showLpInfo ? "Организация контрагент" : "",
    customerRegistrationNumber: !showLpInfo ? "Организация контрагент, ОГРН, ОГРНИП" : "",
    lpCompanyName: !showCustomerInfo ? "Организация поставщик ликвидности" : "",
    lpCompRegNum: !showCustomerInfo ? "Организация поставщик ликвидности, ОГРН, ОГРНИП" : "",
    // destinationWallet: `Кошелек получателя${
    //   bid.network ? ` в сети ${NETWORK_TEXT[bid.network]}` : ""
    // }`,
    amount: "Сумма ЦВ",
    currency: "Тип ЦВ",
    rate: "Курс",
    transitWallet: `Кошелек получателя ${bid.network ? NETWORK_TEXT[bid.network] : ""}`,
    user: "ФИО пользователя",
    date: "Дата",
    amountRub: "Сумма в рублях",
  };

  return labels[key] && value ? (
    <>
      <Typography.Text strong>{labels[key]}:</Typography.Text> {value}{" "}
      {key === "rate" || key === "amountRub" ? "руб." : ""}
    </>
  ) : null;
};
